import React, {useState, useEffect} from 'react';
import {connectSearchBox} from 'react-instantsearch-dom';
import PropTypes from 'prop-types';

import {CloseIcon, SearchIcon} from './icons';

const SearchBox = ({currentRefinement, refine, showModal = null, hideModal = null}) => {
  const [value, setValue] = useState(currentRefinement);
  const [debounceTimer, setDebounceTimer] = useState(null);

  /**
   * Resets the internal InstantSearch query to ''
   * Sets the local state value to '' which resets the input value
   * If the hideModal function prop is passed, execute the function.
   */
  const resetSearchAndCloseModal = () => {
    refine('');
    setValue('');
    hideModal && hideModal();
  };

  /**
   * In order to close the search modal and reset search when a user clicks on the dark
   * underlay of the search modal, we need to listen to the "search:close" event which
   * is dispatched from the QuickSearch component in search.js
   */
  useEffect(() => {
    if (hideModal) {
      window.addEventListener('search:close', resetSearchAndCloseModal);
    }

    return () => {
      if (hideModal) {
        window.removeEventListener('search:close', resetSearchAndCloseModal);
      }
    };
  }, []);

  return (
    <form className="ais-SearchBox-form" noValidate action={`/search?query=${value}`} role="search">
      <input
        type="search"
        placeholder="Search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        name="query"
        required
        maxLength="512"
        className="ais-SearchBox-input"
        value={value}
        onChange={event => {
          /**
           * We need to persist the event so we can deal with it's value asynchronously.
           * https://reactjs.org/docs/events.html
           */
          event.persist();

          /**
           * Set/reset a timeout each time a user types in the output.
           * Only triger the internal query refine if there hasn't been a change after 500ms.
           */
          clearTimeout(debounceTimer);
          setDebounceTimer(setTimeout(() => refine(event.target.value), 500));

          setValue(event.target.value);
          showModal && showModal();
        }}
      />
      <button
        type="reset"
        title="Clear the search query."
        className="ais-SearchBox-reset"
        hidden={currentRefinement === '' ? true : false}
        onClick={resetSearchAndCloseModal}
      >
        <CloseIcon />
      </button>
      <div className="ais-SearchBox-submit" hidden={currentRefinement === '' ? false : true}>
        <SearchIcon />
      </div>
    </form>
  );
};

SearchBox.propTypes = {
  currentRefinement: PropTypes.string,
  refine: PropTypes.func,
  showModal: PropTypes.func,
  hideModal: PropTypes.func,
  close: PropTypes.bool,
};

const DebouncedSearchBox = connectSearchBox(SearchBox);

export default DebouncedSearchBox;
